import { useEffect, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { DataGridPro as DataGrid } from '@mui/x-data-grid-pro';
import Warning from '@mui/icons-material/Warning';
import { difference } from 'lodash-es';
import ConfirmationDialog from '../../components/ConfirmationDialog/ConfirmationDialog';
import { TableCell } from '../../components/table/Table';
import * as yieldService from '../../services/yield-service';
import { useAlerts } from '../../hooks/use-alerts';
import { useGrowers } from '../../hooks/use-growers';

export function YieldRemoval(props) {
  const { onClose, year, aaids } = props;
  const { showInfo, showError } = useAlerts();
  const { selectedId: growerId } = useGrowers();
  const [rawFiles, setRawFiles] = useState();
  const [selectedRawFiles, setSelectedRawFiles] = useState([]);

  useEffect(() => {
    yieldService.findRawFiles(growerId, year, aaids).then(setRawFiles);
  }, [aaids, growerId, year]);

  return (
    <ConfirmationDialog
      title="Remove Yield"
      renderBody={buildRawFiles}
      handleClose={onClose}
      handleConfirm={removeYield}
      canConfirm={Boolean(rawFiles)}
      width="md"
    />
  );

  async function removeYield() {
    try {
      await yieldService.remove(growerId, year, aaids, selectedRawFiles);
      showInfo('Yield removal has been scheduled');
      onClose();
    } catch (e) {
      showError(e);
    }
  }

  function buildRawFiles() {
    return (
      <Box sx={{ height: '50vh', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle2" sx={{ pb: 1 }}>
          Select any of the intersecting raw files to remove
        </Typography>
        <DataGrid
          checkboxSelection
          rowSelectionModel={selectedRawFiles}
          onRowSelectionModelChange={setSelectedRawFiles}
          loading={!rawFiles}
          columns={buildColumns()}
          rows={rawFiles || []}
          initialState={{
            sorting: { sortModel: [{ field: 'filename', sort: 'asc' }] },
          }}
          localeText={{
            noRowsLabel: 'No raw files found',
            footerTotalRows: 'Total:',
            footerRowSelected: (count) => `${count.toLocaleString()} selected`,
          }}
        />
      </Box>
    );
  }

  function buildColumns() {
    return [
      { field: 'filename', headerName: 'File', flex: 1.5 },
      { field: 'crop', headerName: 'Crop', flex: 1 },
      { field: 'actionDate', headerName: 'Harvest Date', flex: 1 },
      {
        field: 'fields',
        headerName: 'Fields',
        flex: 3,
        renderCell: (params) => {
          const foreignAaids = difference(
            params.row.fields.map((f) => f.aaid),
            aaids,
          );
          const foreignFields = params.row.fields
            .filter((f) => foreignAaids.includes(f.aaid))
            .map((f) => f.name);

          return (
            <TableCell renderCellParams={params}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {foreignFields.length ? (
                  <Tooltip
                    title={`Bounding box intersects ${foreignFields.join(', ')} as well`}
                  >
                    <Warning color="warning" />
                  </Tooltip>
                ) : null}
                <Box sx={{ flex: 1 }}>{params.formattedValue}</Box>
              </Box>
            </TableCell>
          );
        },
        valueFormatter: ({ value: fields }) =>
          fields.map((field) => field.name).join(', '),
      },
    ];
  }
}
